import { FareModalProps, Plan } from "@/src/components/desktop/srp/fareFamily/types";
import { useFareFamilyPlanSync, useFareFilterToggle } from "@/src/hooks/srp/fareFamilySRP/useFareFamilyPlanSync";
import { keyframes } from '@emotion/react';
import { Box, Paper, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import FareFamilyBody from "./FareFamilyBody";
import FareFamilyFooter from "./FareFamilyFooter";
import FareFamilyHeader from "./FareFamilyHeader";
import FareRulesDrawer from "./FareRulesDrawer/SRPFareRulesDrawerWrapper";
const scrollBounce = keyframes`
  0%, 100% {
    transform: translateX(-50%) translateY(0);
  }
  50% {
    transform: translateX(-50%) translateY(6px);
  }
`;
const FareFamilyMobile = ({
    plans,
    fareFamilySectors,
    handleClose,
    handleFareFamilyBookClick,
    farePlanState,
    setFarePlanState,
    jsons,
    flight,
}: FareModalProps) => {
    const [hasUserScrolled, setHasUserScrolled] = useState(false);
    const [fareRulesDrawerOpen, setFareRulesDrawerOpen] = useState(false);
    const [selectedPlanForRules, setSelectedPlanForRules] = useState<Plan | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const t = useTranslations('common');

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const handleScroll = () => {
            if (!hasUserScrolled && el.scrollTop > 10) {
                setHasUserScrolled(true);
            }
        };

        el.addEventListener("scroll", handleScroll);
        return () => el.removeEventListener("scroll", handleScroll);
    }, [hasUserScrolled]);

    useFareFamilyPlanSync({
        plans,
        selectedFlightIndex: farePlanState.selectedFlightIndex,
        selectedPlanFareKey: farePlanState.selectedPlanFareKey,
        selectedFilters: farePlanState.selectedFilters,
        setFarePlanState,
        currentFilteredPlans: farePlanState.filteredPlans,
    });

    const handleFilterChange = useFareFilterToggle(setFarePlanState);

    const selectedPlan = farePlanState.filteredPlans.find(
        p => p.plan.fareKey === farePlanState.selectedPlanFareKey
    )?.plan;

    const handleInfoClick = (plan: Plan) => {
        setSelectedPlanForRules(plan);
        setFareRulesDrawerOpen(true);
    };

    const handleFareRulesDrawerClose = () => {
        setFareRulesDrawerOpen(false);
        // Delay clearing the plan so the drawer can animate out
        // and its history hook can clean up the popstate entry properly
        setTimeout(() => setSelectedPlanForRules(null), 300);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                width: "100%",
                fontSize: "12px",
                border: 'none',
            }}
        >
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    flexShrink: 0,
                    padding: '0px 12px 12px',
                    borderBottom: '1px solid lightgray',
                }}
            >
                <FareFamilyHeader
                    handleClose={handleClose}
                    selectedFilters={farePlanState.selectedFilters}
                    onFilterChange={handleFilterChange}
                    selectedFlightIndex={farePlanState.selectedFlightIndex}
                    setSelectedFlightIndex={(index) =>
                        setFarePlanState((prev) => ({
                            ...prev,
                            selectedFlightIndex: index
                        }))
                    }
                    fareFamilySectors={fareFamilySectors}
                    jsons={jsons}
                    flight={flight}
                />
            </Box>

            <Box
                ref={scrollRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch",
                    px: 1,
                    position: "relative",
                    '&::-webkit-scrollbar': {
                        display: 'none'
                    },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    padding: '12px',
                }}
            >
                {!hasUserScrolled && farePlanState.filteredPlans.length > 2 && (
                    <Box
                        sx={(theme) => ({
                            position: "absolute",
                            bottom: "26px",
                            right: "10%",
                            backgroundColor: theme.palette.primary.main,
                            color: "white",
                            borderRadius: "50%",
                            padding: "3px 12px",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                            zIndex: 5,
                            pointerEvents: "none",
                            animation: `${scrollBounce} 1.4s infinite`,
                            opacity: hasUserScrolled ? 0 : 1,
                            transition: 'opacity 0.3s ease',
                        })}
                    >
                        <Typography variant="h6">↓</Typography>
                    </Box>
                )}

                <FareFamilyBody
                    filteredPlans={farePlanState.filteredPlans}
                    selectedFareKey={farePlanState.selectedPlanFareKey}
                    setSelectedFareKey={(key) =>
                        setFarePlanState((prev) => ({
                            ...prev,
                            selectedPlanFareKey: key
                        }))
                    }
                    onInfoClick={handleInfoClick}
                />
            </Box>

            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    zIndex: 10,
                    backgroundColor: "#fff",
                    flexShrink: 0,
                    borderTop: '1px solid lightgray',
                    padding: '8px',
                }}
            >
                {selectedPlan && (
                    <FareFamilyFooter
                        selectedPlan={selectedPlan}
                        handleFareFamilyBookClick={() => 
                        {
                            handleClose()
                            handleFareFamilyBookClick(selectedPlan)
                        }
                        }
                    />
                )}
            </Box>

            <FareRulesDrawer 
                open={fareRulesDrawerOpen}
                onClose={handleFareRulesDrawerClose}
                plan={selectedPlanForRules}
                t={t}
            />
        </Paper>
    );
};

export default FareFamilyMobile;